import { createEntityAdapter, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import { ContentTemplate } from 'src/features/templates/templates.types';
import {
  getCompanyOfflineTemplate,
  setCompanyOfflineTemplate,
  removeCompanyOfflineTemplate,
} from 'src/features/templates/templates.api';

const name = 'offlineTemplate';

interface CompanyOfflineTemplateEntity extends ContentTemplate {
  companyId: string;
}

const offlineTemplateEntityAdapter = createEntityAdapter<AsyncEntity<CompanyOfflineTemplateEntity>>({
  selectId: entity => entity.companyId,
});

export const offlineTemplateSelectors = offlineTemplateEntityAdapter.getSelectors();

export const fetchCompanyOfflineTemplate = createAsyncThunk(
  `${name}/fetchCompanyOfflineTemplate`,
  async ({ companyId }: { companyId: string }, { rejectWithValue }) => {
    try {
      const response = await getCompanyOfflineTemplate({ companyId });
      if (response.data && response.data.template) {
        const { template } = response.data;
        return { ...template, companyId } as CompanyOfflineTemplateEntity;
      } else {
        return { id: null, companyId } as any;
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const assignCompanyOfflineTemplate = createAsyncThunk(
  `${name}/assignCompanyOfflineTemplate`,
  async ({ companyId, templateId }: { companyId: string; templateId: string }, { rejectWithValue }) => {
    try {
      const response = await setCompanyOfflineTemplate({ companyId, templateId });
      return { ...response.data, companyId } as CompanyOfflineTemplateEntity;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const clearCompanyOfflineTemplate = createAsyncThunk(
  `${name}/clearCompanyOfflineTemplate`,
  async ({ companyId }: { companyId: string }, { rejectWithValue }) => {
    try {
      await removeCompanyOfflineTemplate({ companyId });
      return { companyId };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

const offlineTemplateSlice = createSlice({
  name,
  initialState: offlineTemplateEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchCompanyOfflineTemplate.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyOfflineTemplate.fulfilled, (state, { payload }) => {
      if (payload.id) {
        offlineTemplateEntityAdapter.upsertOne(state, payload);
      } else {
        offlineTemplateEntityAdapter.removeOne(state, payload.companyId);
      }
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyOfflineTemplate.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });

    builder.addCase(assignCompanyOfflineTemplate.fulfilled, (state, { payload }) => {
      offlineTemplateEntityAdapter.upsertOne(state, payload);
    });

    builder.addCase(clearCompanyOfflineTemplate.fulfilled, (state, { payload }) => {
      offlineTemplateEntityAdapter.removeOne(state, payload.companyId);
    });
  },
});

const { reducer } = offlineTemplateSlice;

export default reducer;
